/* Case-aggregation discount orchestrator.
   ============================================================================
   Phase 3B-1 Part 4b-2 — storefront wiring for case-aggregation pricing.

   Subscribes to 'circadia:cart-changed' (dispatched by circadia-chrome.js's
   refreshDrawer). On each event:

     1. Fetch /cart.js (current state, including attributes + applied codes).
     2. Show predictor shimmer when the cart's predicted can count crosses the
        first-case threshold (24 cans), bridging the user-perceived latency
        between the cart change and the engine's response.
     3. POST line_items to /apps/dashboard/cart/case-aggregation (App Proxy,
        anonymous). Server is authoritative on price + tier ladder.
     4. Reconcile: compare the engine's discount_pct against the cart
        attribute case_aggregation_pct. If they disagree, mint a new
        DiscountCodeNode via /apps/dashboard/cart/mint-discount-code, then
        apply via POST /cart/update.js {discount, attributes}. Refresh the
        drawer so Shopify's native cart.cart_level_discount_applications
        block renders the new discount line.
     5. If engine.discount_pct === 0 and a case-agg code is currently
        applied, clear it (discount: "", attributes wiped).
     6. Always refresh the progress nudge ("Add N case-equivalent four-packs
        to reach Y% off") regardless of mint/apply outcome.

   Loop avoidance:
     refreshDrawer dispatches 'circadia:cart-changed' on completion. On the
     re-entry pass, the cart attribute case_aggregation_pct now matches the
     engine's discount_pct (and the same code is applied), so the mint/apply
     branch short-circuits — no second refresh, loop terminates.

   ----------------------------------------------------------------------------
   Predictor (Q1b stopgap):
     The Liquid item shape from /cart.js doesn't expose can_count directly
     (the circadia.can_count variant metafield is not set to storefront-read
     access yet — proper exposure is a follow-up task). Until then, we parse
     item.title / item.variant_title for known pack patterns (4-pack, 12-
     pack, case) to predict each line's case-equivalence. The predictor's
     ONLY purpose is to decide whether to show the loading shimmer — the
     authoritative tier math comes from the server.

   ----------------------------------------------------------------------------
   No external dependencies. IIFE, ES5-ish for broad browser support, matches
   the surrounding circadia-chrome.js style. */

(function () {
  'use strict';

  // ─── Config ────────────────────────────────────────────────────────────────

  // App Proxy paths under livecircadia.ca/apps/dashboard/* — routed by
  // Shopify to the Node backend's /proxy/* router with the HMAC params
  // appended. Anonymous endpoints (no logged_in_customer_id required).
  var ENGINE_URL = '/apps/dashboard/cart/case-aggregation';
  var MINT_URL   = '/apps/dashboard/cart/mint-discount-code';

  // Threshold where the predictor begins showing the shimmer (one full case).
  var SHIMMER_THRESHOLD_CANS = 24;

  // Cart attribute names — keep stable; clearing them sets the value to "".
  // ATTR_VALIDATION carries the JSON payload the Part 4b-3 Discount
  // Validation Function reads at checkout to verify the cart still qualifies
  // for the applied tier. Schema:
  //   { version: 1, required_eligible_can_count: N, minted_at: ISO8601 }
  // Bumping the schema requires bumping SUPPORTED_PAYLOAD_VERSION in
  // circadia-extensions/extensions/case-aggregation-validator/src/
  // cart_validations_generate_run.js in tandem.
  var ATTR_CODE        = 'case_aggregation_code';
  var ATTR_PCT         = 'case_aggregation_pct';
  var ATTR_TOTAL_CASES = 'case_aggregation_total_cases';
  var ATTR_VALIDATION  = 'case_aggregation_validation';

  // Canonical case size — 24 cans per case. Matches the backend's
  // CANONICAL_CASE_SIZE_CANS in app/src/shopify/discount-codes.ts. Mixed
  // case sizes are a Phase 6+ concern; if you change this here you also
  // change it there and in the validator function.
  var CANONICAL_CASE_SIZE_CANS = 24;

  // ─── Predictor — pack pattern → can count ──────────────────────────────────
  //
  // Two-layer fallback:
  //
  //   1. Title parsing — works if a product title or variant title ever
  //      contains an explicit pack descriptor ("4-pack", "12-pack",
  //      "case"). Future-proof path; not relied on by today's catalog.
  //
  //   2. SKU suffix parsing — falls back to the deterministic -Npk
  //      convention encoded in app/config/can-count-rules.yaml + used
  //      by the bundle-builder Liquid fix (commit 4a0bce8). Today's
  //      production catalog has titles like "Drift — Blackberry Plum
  //      Chamomile" with variant_title = null, so layer (1) returns 0
  //      and layer (2) is the working signal.
  //
  // Returns the count of cans the item likely represents, or 0 when no
  // pattern matches. The predictor is intentionally permissive: false
  // positives just show the shimmer briefly; false negatives just delay
  // the shimmer until the engine response lands. Either way the server
  // math is authoritative.

  function predictCansFromTitle(title) {
    if (!title) return 0;
    var t = title.toLowerCase();
    // Order matters: case is the biggest, check first; 12-pack before 4-pack.
    if (/\bcase\b/.test(t)) return 24;
    if (/\b12[-\s]?pack\b/.test(t)) return 12;
    if (/\b4[-\s]?pack\b/.test(t)) return 4;
    return 0;
  }

  function predictCansFromSku(sku) {
    if (!sku) return 0;
    var m = sku.match(/-(\d+)pk$/i);
    if (!m) return 0;
    var n = parseInt(m[1], 10);
    // Defensive: only accept known pack sizes. Other suffixes (e.g.,
    // a hypothetical -8pk variety pack) would route through the engine's
    // canonical math via the variant cache; the predictor wouldn't be
    // confident enough to shimmer.
    if (n === 4) return 4;
    if (n === 12) return 12;
    if (n === 24) return 24;
    return 0;
  }

  function predictCansFromItem(item) {
    // Try title-based first (future path).
    var fromTitle = predictCansFromTitle((item.title || '') + ' ' + (item.variant_title || ''));
    if (fromTitle > 0) return fromTitle;
    // Fall back to SKU suffix (current-catalog path).
    return predictCansFromSku(item.sku);
  }

  function predictedTotalCans(cart) {
    var total = 0;
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      // Only count items that look like functional beverages by product type;
      // the predictor's false-positive cost is low (briefly shows shimmer),
      // but checking product_type screens out apparel / merch / accessories.
      if (item.product_type !== 'Functional Beverage') continue;
      total += predictCansFromItem(item) * (item.quantity || 1);
    }
    return total;
  }

  // ─── Network helpers ───────────────────────────────────────────────────────

  function fetchCart() {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); });
  }

  function buildLineItems(cart) {
    // The engine expects variant GIDs. cart.items[].variant_id is the bare
    // numeric id; prepend the gid prefix to match the engine's contract.
    var out = [];
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      if (typeof item.variant_id !== 'number' && typeof item.variant_id !== 'string') continue;
      out.push({
        variant_id: 'gid://shopify/ProductVariant/' + item.variant_id,
        quantity:   item.quantity || 0,
      });
    }
    return out;
  }

  function postEngine(lineItems) {
    return fetch(ENGINE_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ line_items: lineItems }),
    }).then(function (r) {
      if (!r.ok) return r.text().then(function (t) {
        throw new Error('engine HTTP ' + r.status + ': ' + t);
      });
      return r.json();
    });
  }

  function postMint(discountPct, totalCaseEquivalents, cartToken) {
    return fetch(MINT_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        discount_pct: discountPct,
        total_case_equivalents: totalCaseEquivalents,
        cart_token: cartToken || null,
      }),
    }).then(function (r) {
      if (!r.ok) return r.text().then(function (t) {
        throw new Error('mint HTTP ' + r.status + ': ' + t);
      });
      return r.json();
    });
  }

  function postCartUpdate(body) {
    return fetch((window.routes && window.routes.cart_update_url ? window.routes.cart_update_url : '/cart/update') + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body),
    }).then(function (r) { return r.json(); });
  }

  // ─── UI bits ───────────────────────────────────────────────────────────────

  function showShimmer() {
    var el = document.querySelector('[data-cart-predictor]');
    if (el) el.hidden = false;
  }
  function hideShimmer() {
    var el = document.querySelector('[data-cart-predictor]');
    if (el) el.hidden = true;
  }

  // ─── Mint placeholder ──────────────────────────────────────────────────
  //
  // Shown ONLY when Case 3 is about to mint, which is the ~500 ms window the
  // Shopify Admin API write occupies. Case 1 (no discount) and Case 2 (already
  // applied, no re-mint) never show it, so an ordinary add does not flicker.
  //
  // Distinct from the predictor shimmer above: that covers the ENGINE call and
  // is always cleared the moment the verdict lands (hideShimmer at the top of
  // the postEngine handler). This covers the MINT that may follow. They never
  // overlap.
  //
  // querySelectorAll, not querySelector: the drawer and the cart page both
  // render the shared cart-case-aggregation-block snippet, and on /cart both
  // are in the DOM at once.
  function showMintPlaceholder() {
    var els = document.querySelectorAll('[data-cart-mint-placeholder]');
    for (var i = 0; i < els.length; i++) els[i].hidden = false;
  }
  function hideMintPlaceholder() {
    var els = document.querySelectorAll('[data-cart-mint-placeholder]');
    for (var i = 0; i < els.length; i++) els[i].hidden = true;
  }

  function setNudge(text) {
    var el = document.querySelector('[data-cart-nudge]');
    if (!el) return;
    if (text) {
      el.textContent = text;
      el.hidden = false;
    } else {
      el.textContent = '';
      el.hidden = true;
    }
  }

  // ─── Checkout block (Q5) ───────────────────────────────────────────────────
  //
  // When the case-aggregation backend fails (engine, mint, or apply) AND the
  // cart contains functional-beverage items, block checkout so the customer
  // doesn't proceed at undiscounted prices on what is supposed to be a
  // discounted cart. Merch-only carts (no eligible items) proceed normally
  // even on backend failure — there's no discount to lose.
  //
  // Signal surface: the drawer's submit button gets disabled + a .checkout-
  // blocked marker class, and an error message is shown directly above it.
  // The block clears on the next successful reconcile (e.g. user refreshes
  // the page or the cart changes and the engine path succeeds).

  var CHECKOUT_ERROR_TEXT =
    'We had trouble calculating your case discount. Please refresh the page and try again.';

  // Returns true when at least one cart item is case-aggregation-eligible by
  // the same product_type signal the predictor uses. Kept here so the block
  // decision and the shimmer decision share one definition of "eligible".
  function hasEligibleItems(cart) {
    if (!cart || !cart.items) return false;
    for (var i = 0; i < cart.items.length; i++) {
      if (cart.items[i].product_type === 'Functional Beverage') return true;
    }
    return false;
  }

  function checkoutButton() {
    // Drawer's checkout submit — matches the markup in
    // theme/snippets/cart-drawer.liquid: <button type="submit"
    // name="checkout" class="btn btn-primary btn-block">.
    var drawer = document.querySelector('[data-cart-drawer]');
    if (!drawer) return null;
    return drawer.querySelector('button[name="checkout"]');
  }

  function blockCheckout() {
    var btn = checkoutButton();
    if (btn) {
      btn.disabled = true;
      btn.classList.add('checkout-blocked');
      btn.setAttribute('aria-disabled', 'true');
    }
    var errEl = document.querySelector('[data-cart-checkout-error]');
    if (errEl) {
      errEl.textContent = CHECKOUT_ERROR_TEXT;
      errEl.hidden = false;
    }
  }

  function unblockCheckout() {
    var btn = checkoutButton();
    if (btn) {
      btn.disabled = false;
      btn.classList.remove('checkout-blocked');
      btn.removeAttribute('aria-disabled');
    }
    var errEl = document.querySelector('[data-cart-checkout-error]');
    if (errEl) {
      errEl.textContent = '';
      errEl.hidden = true;
    }
  }

  // Build the nudge string from the engine response. Mirrors the bundle-
  // builder tier ladder (10/15/20/25), so the messaging reads consistently
  // between the in-app bundle UI and the case-aggregation drawer flow.
  function nudgeMessage(engineResult, codeApplied) {
    var pct = engineResult.discount_pct;
    var cases = engineResult.total_case_equivalents;
    if (!cases || cases === 0) {
      // No completed case yet. Don't surface a nudge until the user has at
      // least one case in the cart — keeps the drawer quiet for casual
      // single-can browsing.
      return '';
    }
    var TIERS = [
      { n: 1, pct: 10 },
      { n: 2, pct: 15 },
      { n: 3, pct: 20 },
      { n: 4, pct: 25 },
    ];
    var next = null;
    for (var i = 0; i < TIERS.length; i++) {
      if (cases < TIERS[i].n) { next = TIERS[i]; break; }
    }
    // Disclosure appended only when case pricing is ACTUALLY ON THE CART.
    //
    // ⚠ CORRECTED. This was gated on engine pct > 0, which is honest about what
    // the cart EARNS and dishonest about what it RECEIVES. Those diverge
    // exactly when the code has been displaced from the cart's single discount
    // slot, and in that state the line asserted "Case pricing applies to this
    // order." while no case discount was in the total. Now driven by
    // codeApplied, which the caller derives from cart.discount_codes.
    //
    // It exists because CASE- codes carry usageLimit 1 and so apply to the
    // order they are minted on, NOT to later subscription deliveries, while
    // subscribe-and-save recurs (it is a selling-plan pricing policy). Without
    // this line a subscriber reasonably reads the two as behaving the same.
    // Recurrence is Phase 9.3, post-launch; until then this sentence is the
    // whole disclosure. See IMPLEMENTATION_PHASES.md Phase 9.
    var applied = codeApplied ? ' Case pricing applies to this order.' : '';

    if (!next) {
      // Top of the ladder; nothing more to unlock.
      return 'Maxed out at ' + pct + '% off.' + applied;
    }
    var gap = next.n - cases;
    var delta = next.pct - pct;
    return 'Add ' + gap + ' more case' + (gap === 1 ? '' : 's') +
           ' to reach ' + next.pct + '% off (+' + delta + '%).' + applied;
  }

  // ─── Reconcile: mint + apply / clear ───────────────────────────────────────

  function getAttr(cart, name) {
    if (!cart || !cart.attributes) return '';
    var v = cart.attributes[name];
    return v == null ? '' : String(v);
  }

  // Phase 5-γ Commit 4 — detect a CC- credit code on the cart. Used by
  // Case 1 (skip clearApplied when CC- is on cart — its {discount: ''}
  // would nuke the customer's credit code along with the stale CASE-
  // attributes). Case 3 still fires normally: when both apply, CASE-
  // wins on the cart and replaces the CC- code via /cart/update.js,
  // and the displaced CC- becomes available again through the credit
  // row's MODE B Copy Code button so the customer can paste it at
  // Shopify Checkout where combinesWith=true lets the two codes stack.
  function ccCodeOnCart(cart) {
    var codes = (cart && cart.discount_codes) || [];
    return codes.some(function (c) {
      return c && c.applicable && /^CC-[a-f0-9]{32}$/.test(c.code);
    });
  }

  // True when the cart already has the right discount code applied for the
  // engine's current verdict. The pct attribute is the cheap comparator —
  // total_case_equivalents would also work, but pct + total_cases together
  // is the strict identity (engine could land on the same pct from two
  // different case counts). ATTR_VALIDATION must also be present so the
  // Discount Validation Function (Part 4b-3) has the rules it needs at
  // checkout — codes minted before this attribute existed force a re-apply.
  /**
   * Is the expected CASE- code ACTUALLY on the cart right now?
   *
   * cart.discount_codes is Shopify's per-cart applied-codes array, each entry
   * carrying `code` and an `applicable` boolean. It is the authoritative
   * signal (cart_level_discount_applications only carries `title`, and even
   * that did not match reliably; see the note at circadia-chrome.js:1375).
   * `applicable` is required because a present-but-inapplicable code is not
   * discounting anything.
   */
  function caseCodeOnCart(cart, expectedCode) {
    if (!expectedCode) return false;
    var codes = (cart && cart.discount_codes) || [];
    var want = String(expectedCode).toUpperCase();
    return codes.some(function (d) {
      // Shopify normalizes codes at apply time, hence the case-insensitive
      // compare (same standard as the promo-code matcher).
      return d && d.applicable && d.code && String(d.code).toUpperCase() === want;
    });
  }

  /**
   * True when the cart already has the right discount code applied for the
   * engine's current verdict.
   *
   * ⚠ THE CODE CHECK IS LOAD-BEARING, not belt-and-braces. This used to read
   * ONLY the cart attributes, which made them a PROXY for "the code is
   * applied". Any write to the cart's single discount slot breaks that proxy
   * silently, because the writer replaces the code without touching our
   * attributes. Applying store credit is one such writer (it posts
   * {discount: 'CC-...'}), and it will not be the last.
   *
   * The consequence was that a displaced CASE- never came back: attributes
   * still matched, this returned true, the reconciler took Case 2 and
   * short-circuited, and the customer silently lost the case discount with no
   * way to recover short of clearing the cart.
   *
   * Verifying the code against cart.discount_codes makes the displacement
   * SELF-HEALING: whatever removes CASE-, the next reconciler pass re-mints it.
   */
  function alreadyApplied(cart, engineResult) {
    var appliedCode       = getAttr(cart, ATTR_CODE);
    var appliedPct        = getAttr(cart, ATTR_PCT);
    var appliedCases      = getAttr(cart, ATTR_TOTAL_CASES);
    var appliedValidation = getAttr(cart, ATTR_VALIDATION);
    if (!appliedCode) return false;
    if (!appliedValidation) return false;
    if (parseInt(appliedPct, 10) !== engineResult.discount_pct) return false;
    if (parseInt(appliedCases, 10) !== engineResult.total_case_equivalents) return false;
    // Attributes agree with the engine. Now confirm the code they name is
    // genuinely on the cart; stale attributes must not suppress a re-mint.
    if (!caseCodeOnCart(cart, appliedCode)) return false;
    return true;
  }

  function clearApplied() {
    return postCartUpdate({
      discount: '',
      attributes: {
        case_aggregation_code: '',
        case_aggregation_pct: '',
        case_aggregation_total_cases: '',
        case_aggregation_validation: '',
      },
    });
  }

  // The validation payload is computed client-side from the same canonical
  // case size the backend uses (CANONICAL_CASE_SIZE_CANS = 24). minted_at
  // is included for ops/forensics; the function ignores it.
  function buildValidationPayload(totalCases) {
    return JSON.stringify({
      version: 1,
      required_eligible_can_count: totalCases * CANONICAL_CASE_SIZE_CANS,
      minted_at: new Date().toISOString(),
    });
  }

  function applyMinted(code, pct, totalCases) {
    return postCartUpdate({
      discount: code,
      attributes: {
        case_aggregation_code: code,
        case_aggregation_pct: String(pct),
        case_aggregation_total_cases: String(totalCases),
        case_aggregation_validation: buildValidationPayload(totalCases),
      },
    });
  }

  // Drawer-only refresh — reverted from the b41b486 attempt to delegate
  // to window.Circadia.refreshCart (which would have synced /cart too).
  // The delegation broke the engine in smoke (no reconcile happened on
  // a 4-case cart; UI stuck at "0/4 / NO TIER DISCOUNT YET"). Root
  // cause not pinned, but the doubled section-render + dual circadia:
  // cart-changed dispatch may have interacted with reconcile's inFlight
  // guard or Shopify's section-render cache in a way the single-fetch
  // path didn't surface. Reverting case-aggregation.js to its original
  // drawer-only behavior restores the engine. The bidirectional cart-
  // page sync from a case-agg-driven reconcile is now a knowingly
  // accepted gap: the cart-page tier bar reflects whatever state
  // existed at the LAST user action; case-agg auto-applications on
  // /cart visit / mutation won't surface on the cart page until the
  // user next triggers a refreshCart-routed action (qty +/-, remove,
  // promo apply, etc.) or hard-refreshes /cart.
  function refreshDrawer() {
    if (window.Circadia && typeof window.Circadia.refreshDrawer === 'function') {
      return window.Circadia.refreshDrawer();
    }
    return Promise.resolve();
  }

  // ─── Main reconcile flow ───────────────────────────────────────────────────

  // Single-flight guard, with COALESCING.
  //
  // ⚠ WHY THIS IS NOT A BARE `return`. It used to be: a
  // 'circadia:cart-changed' arriving during an in-flight reconcile was
  // DISCARDED ENTIRELY, and the comment claimed "the next one will pick up the
  // latest cart state". There is no next one if the customer stops acting. So
  // a quantity change during the ~500 ms mint left the customer holding a
  // discount computed against the PREVIOUS cart, potentially a HIGHER
  // percentage than the cart earns, silently and in both directions.
  //
  // COALESCE, DO NOT ACCUMULATE: any number of changes during one pass set a
  // single flag, which produces exactly ONE follow-up pass. Holding the + button
  // yields one follow-up per completed pass, never one per event.
  var inFlight = false;
  var pendingReconcile = false;

  // Defensive bound on CONSECUTIVE follow-ups. Termination is argued in the
  // terminal link below and does not depend on this; it is a backstop against
  // an unforeseen non-converging state on a money path, not the mechanism.
  // Reset whenever a pass completes with nothing pending.
  var consecutiveFollowUps = 0;
  var MAX_CONSECUTIVE_FOLLOW_UPS = 5;

  function reconcile() {
    if (inFlight) {
      // Do not drop it: remember that the cart moved and re-run once this
      // pass settles.
      pendingReconcile = true;
      return;
    }
    inFlight = true;

    // Snapshot the cart in scope so the .catch handler can check eligibility
    // for the block-checkout decision (Q5). If fetchCart() itself throws,
    // cartSnapshot stays null and the catch falls through to unblockCheckout
    // (we can't say whether the cart has eligible items, so don't block).
    var cartSnapshot = null;

    // Track whether this reconcile actually mutated cart state. Only
    // state-changing branches (case 1 + existing code → clear; case 3
    // mint+apply) set this to true. The post-reconcile circadia:case-
    // aggregation-reconciled event is dispatched only when true so the
    // cart-page consumer in circadia-chrome.js can refreshCartPage() —
    // refreshCartPage's own dispatch of circadia:cart-changed would
    // re-trigger reconcile, so unconditional dispatch would infinite-
    // loop (case 2 short-circuit ≠ state change ⇒ no dispatch ⇒ loop
    // breaks after one cycle).
    var stateChanged = false;

    return fetchCart()
      .then(function (cart) {
        cartSnapshot = cart;
        var lineItems = buildLineItems(cart);
        // Show shimmer when predicted cans cross the case threshold AND no
        // discount has been applied yet (avoids shimmering over an already-
        // rendered discount line).
        var alreadyHasCode = !!getAttr(cart, ATTR_CODE);
        if (!alreadyHasCode && predictedTotalCans(cart) >= SHIMMER_THRESHOLD_CANS) {
          showShimmer();
        } else {
          hideShimmer();
        }

        // Empty cart short-circuit — nothing to compute, clear any nudge.
        if (lineItems.length === 0) {
          hideShimmer();
          setNudge('');
          return null;
        }

        return postEngine(lineItems).then(function (engineResult) {
          hideShimmer();
          window.Circadia.caseAggregation.lastVerdict = engineResult;
          setNudge(nudgeMessage(engineResult, alreadyApplied(cart, engineResult)));

          var hasCode = !!getAttr(cart, ATTR_CODE);
          var creditOnCart = ccCodeOnCart(cart);

          // Case 1: engine says no discount; clear any stale applied code.
          // Phase 5-γ Commit 4 — also skip when CC- is on cart: the
          // single-slot constraint means CASE- can't actually be applied
          // alongside CC-, so any ATTR_CODE here is stale-attribute-only.
          // clearApplied()'s {discount: ''} would also nuke the customer's
          // CC- code; better to leave the stale attributes for the next
          // cycle (which fires when the customer removes credit).
          if (engineResult.discount_pct === 0) {
            if (hasCode && !creditOnCart) {
              stateChanged = true;
              return clearApplied().then(refreshDrawer);
            }
            return null;
          }

          // Case 2: engine wants a discount that's already correctly applied.
          if (alreadyApplied(cart, engineResult)) {
            return null;
          }

          // Case 3: engine wants a discount; mint + apply. /cart/update.js
          // {discount: NEW} replaces any code already on the cart — which
          // is the intended behavior even when a CC- credit code was the
          // prior code. Per the Phase 5-γ Commit 4 policy: CASE- wins on
          // the cart when both apply; the customer's CC- code becomes
          // available again as a Copy Code in the credit row's MODE B
          // state so they can paste it at Shopify Checkout where
          // combinesWith=true allows the two codes to stack.
          stateChanged = true;
          // The ~500 ms window starts HERE. Everything before this point is
          // fast enough not to need acknowledging.
          showMintPlaceholder();
          return postMint(
            engineResult.discount_pct,
            engineResult.total_case_equivalents,
            cart.token
          ).then(function (mintResult) {
            return applyMinted(
              mintResult.code,
              engineResult.discount_pct,
              engineResult.total_case_equivalents
            );
          }).then(refreshDrawer);
        });
      })
      .then(function () {
        // Success path — backend agreed with cart state. Clear any prior
        // block from a previous failed reconcile.
        unblockCheckout();
        if (stateChanged) {
          // Signal to circadia-chrome.js's reconciled listener that the
          // cart-page sections should re-render against the new cart
          // attributes. case-aggregation.js's own refreshDrawer above
          // has already updated the drawer; this event extends sync to
          // the /cart page surface without doubling the drawer refresh.
          document.dispatchEvent(new CustomEvent('circadia:case-aggregation-reconciled'));
        }
      })
      .catch(function (err) {
        // Q5: block checkout when the backend failed AND the cart has
        // case-aggregation-eligible items. Merch-only carts proceed normally
        // since there's no discount to miss. If fetchCart itself failed
        // (cartSnapshot is null), we can't tell either way → don't block.
        // eslint-disable-next-line no-console
        if (window.console && window.console.warn) {
          console.warn('[case-aggregation] reconcile failed:', err && err.message ? err.message : err);
        }
        hideShimmer();
        if (cartSnapshot && hasEligibleItems(cartSnapshot)) {
          blockCheckout();
        } else {
          // Merch-only cart, empty cart, or we couldn't fetch the cart.
          // Make sure no stale block from a prior cycle persists.
          unblockCheckout();
        }
      })
      .then(function () {
        // TERMINAL LINK: chained AFTER .catch(), so it runs on EVERY exit,
        // success and failure alike. Same structural guarantee as the cart-add
        // button re-enable (a4d4e12): an early return inside an earlier link
        // cannot skip a later one.
        inFlight = false;

        // ─── Coalesced follow-up ────────────────────────────────────────────
        //
        // WHY THIS TERMINATES, and it does not rely on the cap below:
        //
        //   A pass only dispatches 'circadia:cart-changed' when it MUTATES the
        //   cart, because the dispatch comes from refreshDrawer, which only
        //   Case 1 (clearApplied) and Case 3 (mint + apply) call. Both leave
        //   the cart in the state the engine just asked for. So the follow-up
        //   pass finds attributes matching the verdict AND the code present,
        //   hits Case 2 (alreadyApplied), returns without mutating, and
        //   dispatches nothing. pendingReconcile stays false and the chain
        //   stops. Two passes per add, then quiet.
        //
        //   A failed mint cannot loop either: postMint throwing routes to
        //   .catch(), refreshDrawer is never reached, so nothing dispatches.
        //
        //   The only way to keep going is a cart that genuinely changes on
        //   every pass, which requires continuous customer input and stops when
        //   they do.
        if (pendingReconcile && consecutiveFollowUps < MAX_CONSECUTIVE_FOLLOW_UPS) {
          pendingReconcile = false;
          consecutiveFollowUps += 1;
          // Placeholder deliberately NOT cleared here. If the follow-up mints
          // again it stays continuously visible instead of flickering off and
          // back on; if it does not mint, the follow-up's own terminal link
          // clears it. Either way exactly one link is responsible for clearing.
          reconcile();
          return;
        }
        if (pendingReconcile) {
          // Cap hit. Drop the pending flag rather than looping, and say so:
          // reaching this means the converge argument above was wrong.
          pendingReconcile = false;
          if (window.console && window.console.warn) {
            console.warn(
              '[case-aggregation] follow-up cap reached; dropping a pending reconcile'
            );
          }
        }
        consecutiveFollowUps = 0;
        hideMintPlaceholder();
      });
  }

  // ─── Event wiring ──────────────────────────────────────────────────────────

  document.addEventListener('circadia:cart-changed', function () { reconcile(); });

  // Initial run on load — validates the cart's existing applied code against
  // current engine state. Covers the case where the user lands on the page
  // with a stale code attribute from a previous session, or where cart
  // contents changed between sessions.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { reconcile(); });
  } else {
    reconcile();
  }

  // ─── Checkout guard (client-side pivot for Phase 3B-1 Part 4b-3) ───────────
  //
  // The server-side Cart-and-Checkout Validation function is built + deployed
  // but blocked on Shopify Plus gating (see docs/01_ARCHITECTURE/EDGE_CASES.md
  // EC-035). Until Plus migration, this client-side guard runs on the cart
  // drawer Checkout-button click.
  //
  // Strategy (hybrid D3 — predictor-first, engine-fallback, fail-open):
  //
  //   1. No validation attribute → proceed (no case discount applied)
  //   2. Predictor (predictedTotalCans) ≥ required → proceed (zero-latency)
  //   3. Predictor < required → engine recheck with 1500ms timeout
  //        - engine ≥ required → proceed (predictor false-negative)
  //        - engine < required → clearApplied(), toast, proceed at std price
  //        - engine failed   → fail-open, proceed with discount intact
  //                            (post-hoc audit via fa6d056 metafield)
  //
  // The predictor only ever UNDER-counts (returns 0 on unrecognized SKU
  // patterns), so the engine-fallback path never produces false-positive
  // clears — only false-negatives followed by authoritative recheck.
  //
  // Return shape: { proceed: boolean, removedDiscount: boolean, reason: string }
  // proceed is ALWAYS true (soft guard, never blocks the customer).
  // reason is a short tag for logging/debugging — see contract below.

  var guardInFlight = false;
  var ENGINE_TIMEOUT_MS = 1500;

  /**
   * @returns {Promise<{ proceed: boolean, removedDiscount: boolean, reason: string }>}
   *   reason ∈ { "guard-already-in-flight", "no-validation-attribute",
   *              "predictor-pass", "engine-pass", "engine-confirmed-shortfall",
   *              "engine-failed-fail-open", "parse-error-cleared" }
   */
  async function guardCheckout() {
    if (guardInFlight) {
      return { proceed: true, removedDiscount: false, reason: 'guard-already-in-flight' };
    }
    guardInFlight = true;
    try {
      var cart = await fetchCart();
      var attrRaw = getAttr(cart, ATTR_VALIDATION);
      if (!attrRaw) {
        return { proceed: true, removedDiscount: false, reason: 'no-validation-attribute' };
      }

      // Parse + version-check the validation payload. Corrupted attribute →
      // wipe + treat as "discount removed" so the customer doesn't proceed
      // with a code we can't verify.
      var rules;
      try {
        rules = JSON.parse(attrRaw);
      } catch (_) {
        await clearApplied();
        return { proceed: true, removedDiscount: true, reason: 'parse-error-cleared' };
      }
      if (!rules || rules.version !== 1 || typeof rules.required_eligible_can_count !== 'number') {
        await clearApplied();
        return { proceed: true, removedDiscount: true, reason: 'parse-error-cleared' };
      }
      var required = rules.required_eligible_can_count;

      // Layer 1 — predictor. Zero-latency happy path; under-counts only on
      // unrecognized SKUs (which then route through the engine recheck).
      if (predictedTotalCans(cart) >= required) {
        return { proceed: true, removedDiscount: false, reason: 'predictor-pass' };
      }

      // Layer 2 — authoritative engine recheck with 1500ms timeout.
      // Fail-open on any error (timeout, network, 5xx, parse) per D3 spec.
      var lineItems = buildLineItems(cart);
      if (lineItems.length === 0) {
        // Empty cart with a stale validation attribute. Clear it; the cart
        // can't possibly qualify with no items.
        await clearApplied();
        return { proceed: true, removedDiscount: true, reason: 'engine-confirmed-shortfall' };
      }
      var controller = new AbortController();
      var timeoutId = setTimeout(function () { controller.abort(); }, ENGINE_TIMEOUT_MS);
      var engineResult;
      try {
        var resp = await fetch(ENGINE_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ line_items: lineItems }),
          signal: controller.signal,
        });
        if (!resp.ok) {
          throw new Error('engine HTTP ' + resp.status);
        }
        engineResult = await resp.json();
      } catch (err) {
        if (window.console && window.console.warn) {
          console.warn('[checkout-guard] engine recheck failed, failing open:',
            err && err.message ? err.message : err);
        }
        return { proceed: true, removedDiscount: false, reason: 'engine-failed-fail-open' };
      } finally {
        clearTimeout(timeoutId);
      }

      var authoritativeCans = (engineResult.total_case_equivalents || 0) * CANONICAL_CASE_SIZE_CANS;
      if (authoritativeCans >= required) {
        return { proceed: true, removedDiscount: false, reason: 'engine-pass' };
      }

      // Authoritative shortfall confirmed — clear the discount + attributes.
      try {
        await clearApplied();
      } catch (err) {
        // clearApplied itself failed (rare; /cart/update.js outage). Customer
        // would otherwise proceed with the stale discount — but we have no
        // mechanism to block at this point. Log + proceed; the fa6d056
        // metafield audit trail will catch this.
        if (window.console && window.console.warn) {
          console.warn('[checkout-guard] clearApplied failed during shortfall path:',
            err && err.message ? err.message : err);
        }
      }
      return { proceed: true, removedDiscount: true, reason: 'engine-confirmed-shortfall' };
    } finally {
      guardInFlight = false;
    }
  }

  // Defensive window export — case-aggregation.js loads after circadia-
  // chrome.js per theme.liquid script order, but other code may initialize
  // window.Circadia first. Namespace the guard under .caseAggregation so it
  // doesn't collide with window.Circadia.refreshDrawer from circadia-chrome.
  if (!window.Circadia) window.Circadia = {};
  if (!window.Circadia.caseAggregation) window.Circadia.caseAggregation = {};
  window.Circadia.caseAggregation.guardCheckout = guardCheckout;
  // Last engine verdict, published so the credit row can suppress MODE A
  // whenever case pricing is AVAILABLE (not merely already applied). Undefined
  // until the first engine response lands; the reader falls back conservatively.
  window.Circadia.caseAggregation.lastVerdict = null;
})();
